import {
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableRow,
  CircularProgress,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import LinkStyleButton from '@src/components/LinkStyleButton/LinkStyleButton'
import {
  OrderDetailDocument,
  OrderDetailQuery,
  OrderDetailQueryVariables,
} from '@src/queries/OrderDetailsQuery.generated'
import {
  Order,
  OrderItemTrackingInfo,
  ScheduleLineStatusType,
} from '@src/types/graphql-types'
import React, { useState, useMemo } from 'react'
import { FormattedDate, FormattedMessage } from 'react-intl'
import messages from '../messages'
import { messages as commonMessages } from '@src/utils/messages'
import {
  checkIfDropShip,
  getOrderItemsStatusKey,
} from '../OrderDetail/orderDetailUtils'
import { OrderItemNumber } from '../OrderItem/OrderItemNumber'
import GlobalAlert from '@src/components/GlobalAlert'
import { getDateMessage, getStatusMessage } from './config'
import { groupOrderItems } from './utils'
import { sendEvent } from '@src/utils/analytics/coreAnalytics'
import { QueryHookOptions, useApolloClient, useQuery } from 'react-apollo'
import { ShipmentTrackingCell } from './ShipmentTrackingCell'
import countriesMap from '@localization/countries.json'
import { useCheckUserCountry } from '@src/utils/useCheckUserCountry'
import { GTMEventTypes } from '@sial/common-utils'

const MAX_ITEMS_TRUNCATED = 5

const useStyles = makeStyles((theme) => ({
  loadingIndicator: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    color: theme.palette.grey[700],
    textTransform: 'uppercase',
    '& > * + *': {
      marginLeft: theme.spacing(2),
    },
  },
  errorContainer: {
    marginTop: theme.spacing(3),
  },
  tableContainer: {
    overflow: 'auto',
    borderRadius: 4,
    border: `1px solid ${theme.palette.grey[400]}`,
  },
  table: {
    tableLayout: 'fixed',
  },
  tableBody: {
    '&:first-child > .MuiTableRow-root:first-child': {
      borderTop: 'none',
    },
    '&:last-child > .MuiTableRow-root:last-child': {
      borderBottom: 'none',
    },
    '& > .MuiTableRow-root > .MuiTableCell-root': {
      fontSize: theme.typography.pxToRem(12),
      verticalAlign: 'baseline',
    },
    '& > .MuiTableRow-root:first-child > .MuiTableCell-root:first-child': {
      paddingLeft: theme.spacing(4),
    },
  },
  seeAll: {
    textAlign: 'center',
    marginTop: theme.spacing(6),
  },
}))

interface OrderRowExpansionProps {
  order: Order
  setTrackingInfo: React.Dispatch<
    React.SetStateAction<OrderItemTrackingInfo | null>
  >
  userId: string
}

const orderExpansionQuery = OrderDetailDocument

const getOrderExpansionVariables = (
  order: Order
): OrderDetailQueryVariables => ({
  orderIdentifier: order.salesDocNumber!,
  isAcknowledgement: false,
  sysId: order.sysId,
  checkDeliveryDate: true,
})

export const useOrderExpansionQuery = (
  order: Order,
  options?: QueryHookOptions<OrderDetailQuery, OrderDetailQueryVariables>
) => {
  return useQuery<OrderDetailQuery, OrderDetailQueryVariables>(
    orderExpansionQuery,
    {
      variables: getOrderExpansionVariables(order),
      fetchPolicy: 'cache-and-network',
      ...options,
    }
  )
}

export const useOrderExpansionCache = () => {
  const client = useApolloClient()
  return {
    getOrderDetails: (order: Order) => {
      try {
        return client.readQuery<OrderDetailQuery>({
          query: orderExpansionQuery,
          variables: getOrderExpansionVariables(order),
        })
      } catch {
        console.error(
          `Unable to find item data in cache while generating orders list export for order "${order.salesDocNumber}".`
        )
      }
      return null
    },
  }
}

export const OrderRowExpansion: React.FC<OrderRowExpansionProps> = ({
  order,
  setTrackingInfo,
  userId,
}) => {
  const classes = useStyles()
  const isChinaUser = useCheckUserCountry(countriesMap.CN.countryCode)
  const [truncationEnforced, setTruncationEnforced] = useState(true)
  const { loading, data, error } = useOrderExpansionQuery(order)
  const orderDetails = data?.orderDetail?.order
  const items = data?.orderDetail.order.items
  const orderType = data?.orderDetail.order.orderType
  const groupedItems = useMemo(
    () => groupOrderItems(items ? items : []),
    [items]
  )

  if (loading) {
    return (
      <div className={classes.loadingIndicator}>
        <CircularProgress size={20} thickness={5} />
        <span>
          <FormattedMessage {...messages.LOADING} />
          ...
        </span>
      </div>
    )
  }
  if (error || !items?.length) {
    return (
      <div className={classes.errorContainer}>
        <GlobalAlert severity="error">
          <FormattedMessage {...messages.ORDER_DETAILS_UNAVAILABLE} />
        </GlobalAlert>
      </div>
    )
  }

  const truncated =
    truncationEnforced && groupedItems.length > MAX_ITEMS_TRUNCATED
  const visibleItems = truncated
    ? groupedItems.slice(0, MAX_ITEMS_TRUNCATED)
    : groupedItems

  return (
    <div>
      <TableContainer className={classes.tableContainer}>
        <Table className={classes.table}>
          {visibleItems.map((itemGroup, i) => (
            <TableBody key={i} className={classes.tableBody}>
              {itemGroup.scheduleLines.map((line, j) => {
                const isDropShip = line.status && checkIfDropShip(line.status)
                const tableStatusKey = line.status
                  ? getOrderItemsStatusKey(line.status)
                  : null
                const tableStatusMessage = getStatusMessage(tableStatusKey)
                const dateMessage = getDateMessage(tableStatusKey)

                return (
                  <TableRow key={`${itemGroup.materialNumber}-${j}`}>
                    {j === 0 && (
                      <TableCell
                        component="th"
                        rowSpan={itemGroup.scheduleLines.length}
                      >
                        <OrderItemNumber
                          item={itemGroup}
                          orderType={orderType}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      {!!line.confirmedQuantity && `${line.confirmedQuantity} `}
                      {tableStatusMessage ? (
                        <FormattedMessage {...tableStatusMessage} />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <FormattedMessage {...dateMessage} />:
                      <div>
                        {!isDropShip &&
                        line.status !== ScheduleLineStatusType.Delayed &&
                        line.displayableDate ? (
                          <FormattedDate
                            value={new Date(line.displayableDate)}
                            year="numeric"
                            month="short"
                            day="numeric"
                            timeZone="UTC"
                          />
                        ) : (
                          '-'
                        )}
                      </div>
                    </TableCell>

                    <ShipmentTrackingCell
                      line={line}
                      itemGroup={itemGroup}
                      orderDetails={orderDetails}
                      isChinaUser={isChinaUser}
                      setTrackingInfo={setTrackingInfo}
                    />
                  </TableRow>
                )
              })}
            </TableBody>
          ))}
        </Table>
      </TableContainer>
      {truncated && (
        <div className={classes.seeAll}>
          <LinkStyleButton
            onClick={() => {
              setTruncationEnforced(false)
              sendEvent({
                eventType: GTMEventTypes.AnalyticsEvent,
                payload: {
                  event: 'order_interaction',
                  action: 'see all order items',
                  detail: orderDetails?.orderNumber,
                  section: 'orders',
                  order_type:
                    orderDetails?.orderType === null
                      ? 'standard'
                      : orderDetails?.orderType,
                  component: 'list',
                  element_type: 'link',
                  element_text: `see all (${groupedItems.length})`,
                  link_url: undefined,
                  core_event: 'no',
                  user_detail: undefined,
                  event_group: undefined,
                  event_subgroup: undefined,
                  userId,
                },
              })
            }}
          >
            <FormattedMessage {...commonMessages.SEE_ALL} />
            {` (${groupedItems.length})`}
          </LinkStyleButton>
        </div>
      )}
    </div>
  )
}
